import React, {
   useCallback,
   useEffect,
   useRef,
   useState,
   useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { ReactSVG } from "react-svg";

import addIcon from "../../assets/svg/add-s.svg";
import {
   closeStaffChatWorkspace,
   expandStaffChatWorkspace,
   getStaffChatWorkspaceState,
   retractStaffChatWorkspace,
   subscribeStaffChatWorkspace,
} from "../../stores/staffChatWorkspaceStore";
import { getCurrentPopup, subscribePopup } from "../Common/popupUIStore";
import StaffChatPopup from "./StaffChatPopup";

const RETRACT_STRIP_PX = 76;
const EDGE_GUARD_MS = 70;
const TRANSITION_MS = 260;

function useStaffChatWorkspace() {
   return useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );
}

function isDesktopViewport() {
   return (
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 861px)").matches
   );
}

/**
 * Independent floating host for the Message workspace (left side).
 * Not part of PopupUI — no overlay, can retract while other popups stay normal.
 */
export default function StaffChatWorkspaceHost() {
   const workspace = useStaffChatWorkspace();
   const panelRef = useRef(null);
   const leaveTimerRef = useRef(0);
   const pointerRef = useRef({ x: 0, y: 0 });
   const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
   const [otherPopupOpen, setOtherPopupOpen] = useState(
      () => Boolean(getCurrentPopup()),
   );

   useEffect(() => {
      if (typeof window === "undefined") return undefined;
      const mq = window.matchMedia("(min-width: 861px)");
      const onChange = () => setIsDesktop(mq.matches);
      onChange();
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);
      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, []);

   useEffect(() => {
      if (!workspace.isOpen || !isDesktop) return undefined;
      const onMove = (event) => {
         pointerRef.current = { x: event.clientX, y: event.clientY };
      };
      document.addEventListener("pointermove", onMove, { passive: true });
      return () => document.removeEventListener("pointermove", onMove);
   }, [workspace.isOpen, isDesktop]);

   const clearLeaveTimer = useCallback(() => {
      if (leaveTimerRef.current && typeof window !== "undefined") {
         window.clearTimeout(leaveTimerRef.current);
         leaveTimerRef.current = 0;
      }
   }, []);

   const requestExpand = useCallback(() => {
      if (!isDesktop) return;
      clearLeaveTimer();
      expandStaffChatWorkspace();
   }, [clearLeaveTimer, isDesktop]);

   const requestRetract = useCallback(() => {
      if (!isDesktop) return;
      clearLeaveTimer();
      retractStaffChatWorkspace();
   }, [clearLeaveTimer, isDesktop]);

   // Auto-retract when another (non-message) popup opens.
   useEffect(() => {
      return subscribePopup(() => {
         const popup = getCurrentPopup();
         setOtherPopupOpen(Boolean(popup));
         if (popup && workspace.isOpen && isDesktop) {
            clearLeaveTimer();
            retractStaffChatWorkspace();
         }
      });
   }, [workspace.isOpen, isDesktop, clearLeaveTimer]);

   // Outside pointer → retract immediately (do not close conversation).
   useEffect(() => {
      if (!workspace.isOpen || !isDesktop) return undefined;

      const onPointerDown = (event) => {
         const panel = panelRef.current;
         if (!(panel instanceof Element)) return;
         const target = event.target;
         if (!(target instanceof Node)) return;

         if (panel.contains(target)) {
            if (workspace.dockMode === "retracted") requestExpand();
            return;
         }

         requestRetract();
      };

      document.addEventListener("pointerdown", onPointerDown, true);
      return () => {
         document.removeEventListener("pointerdown", onPointerDown, true);
      };
   }, [
      workspace.isOpen,
      workspace.dockMode,
      isDesktop,
      requestExpand,
      requestRetract,
   ]);

   useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

   if (!workspace.isOpen) return null;

   const retracted = isDesktop && workspace.dockMode === "retracted";
   const style = {
      zIndex: otherPopupOpen ? 1030 : 1045,
      "--staff-chat-strip": `${RETRACT_STRIP_PX}px`,
      "--staff-chat-transition": `${TRANSITION_MS}ms`,
   };

   const onPanelEnter = () => {
      if (!isDesktop) return;
      requestExpand();
   };

   const onPanelLeave = () => {
      if (!isDesktop) return;
      clearLeaveTimer();
      // Confirm the pointer really left — React remounts on list→thread can
      // fire a false mouseleave while the cursor is still on the panel.
      leaveTimerRef.current = window.setTimeout(() => {
         leaveTimerRef.current = 0;
         const panel = panelRef.current;
         if (!(panel instanceof Element)) return;
         const { x, y } = pointerRef.current;
         const under = document.elementFromPoint(x, y);
         if (under instanceof Node && panel.contains(under)) {
            return;
         }
         retractStaffChatWorkspace();
      }, EDGE_GUARD_MS);
   };

   const tree = (
      <div
         ref={panelRef}
         className={
            "staffChatWorkspace" +
            (retracted ? " staffChatWorkspace--retracted" : "") +
            (workspace.view === "thread"
               ? " staffChatWorkspace--conversation"
               : "")
         }
         style={style}
         data-dock={workspace.dockMode}
         onMouseEnter={onPanelEnter}
         onMouseLeave={onPanelLeave}
         role="dialog"
         aria-label="Mesaje"
         aria-modal="false"
      >
         <button
            type="button"
            className="staffChatWorkspace__close"
            onClick={(event) => {
               event.stopPropagation();
               closeStaffChatWorkspace();
            }}
            aria-label="Închide mesajele"
         >
            <ReactSVG className="react-icon rotate45" src={addIcon} />
         </button>

         <div className="staffChatWorkspace__inner">
            <StaffChatPopup
               conversationId={workspace.openProps?.conversationId}
               student={workspace.openProps?.student}
               openThread={Boolean(workspace.openProps?.openThread)}
            />
         </div>
      </div>
   );

   if (typeof document === "undefined") return tree;
   return createPortal(tree, document.body);
}
